import React, { useEffect, useMemo, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowLeft, faEdit, faPlus, faSave, faTags, faTrash } from "@fortawesome/free-solid-svg-icons";
import { Badge, Button, Card, Col, Form, InputGroup, Row } from "@themesberg/react-bootstrap";
import { ReactTabulator } from "react-tabulator";
import Select from "react-select";
import Swal from "sweetalert2";

import geoNow from "../utils/geonow";
import { matchesInstrumentSearch } from "../utils/instrumentCatalog";
import { toInstrumentExtendPayload } from "../utils/instrumentExtends";

const emptySetting = (projectId, pointIdx) => ({
    id: null,
    projectId,
    pointIdx,
    nickname: null,
    lowerScale: null,
    upperScale: null,
    statusStaticNote: null,
    hidden: false,
    longtitude: 0,
    latitude: 0,
    height: 0,
    depthSeriesConfigId: null
});

const escapeHtml = value => String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");

export default function InstrumentCatalogPanel({
    token,
    selectedProject,
    preData,
    pbItems,
    onSavedChanges,
    onSwitchToClassic
}) {
    const [rows, setRows] = useState([]);
    const [dirtyPoints, setDirtyPoints] = useState(new Set());
    const [tags, setTags] = useState([]);
    const [search, setSearch] = useState("");
    const [areaFilter, setAreaFilter] = useState("all");
    const [configuredFilter, setConfiguredFilter] = useState("all");
    const [selectedTagIds, setSelectedTagIds] = useState([]);
    const [sortMode, setSortMode] = useState("pointIdx");
    const [newTagName, setNewTagName] = useState("");
    const [newTagColor, setNewTagColor] = useState("#4f46e5");
    const canEdit = selectedProject.isProjectAdmin;

    const reloadTags = async () => {
        const response = await geoNow.api.instrumentTags.listByProjectId(token, selectedProject.projectId);
        if (response?.status === 1) setTags(response.message || []);
    };

    useEffect(() => {
        reloadTags();
    }, [selectedProject.projectId]);

    useEffect(() => {
        const instruments = pbItems instanceof Map ? Array.from(pbItems.values()) : [];
        const settingsByPoint = new Map();
        (preData || []).forEach(setting => {
            const current = settingsByPoint.get(Number(setting.pointIdx));
            if (!current || (current.depthSeriesConfigId && !setting.depthSeriesConfigId)) {
                settingsByPoint.set(Number(setting.pointIdx), setting);
            }
        });

        const nextRows = instruments
            .filter(item => item.pointIdx != null)
            .map(instrument => {
                const pointIdx = Number(instrument.pointIdx);
                const setting = settingsByPoint.get(pointIdx);
                return {
                    ...(setting ? { ...setting } : emptySetting(selectedProject.projectId, pointIdx)),
                    pointNo: instrument.pointNo || "",
                    area: instrument.area || "",
                    configured: Boolean(setting),
                    hasMultipleSettings: (preData || []).filter(x => Number(x.pointIdx) === pointIdx).length > 1
                };
            });
        setRows(nextRows);
        setDirtyPoints(new Set());
    }, [pbItems, preData, selectedProject.projectId]);

    const tagsByPoint = useMemo(() => {
        const result = new Map();
        tags.forEach(tag => (tag.pointIndices || []).forEach(pointIdx => {
            const pointTags = result.get(Number(pointIdx)) || [];
            pointTags.push(tag);
            result.set(Number(pointIdx), pointTags);
        }));
        return result;
    }, [tags]);

    const displayRows = useMemo(() => {
        const result = rows.map(row => {
            const pointTags = tagsByPoint.get(Number(row.pointIdx)) || [];
            return {
                ...row,
                tagIds: pointTags.map(tag => tag.id),
                tagNames: pointTags.map(tag => tag.name).join("、")
            };
        }).filter(row => {
            if (!matchesInstrumentSearch(row, search)) return false;
            if (areaFilter !== "all" && row.area !== areaFilter) return false;
            if (configuredFilter === "configured" && !row.configured) return false;
            if (configuredFilter === "unconfigured" && row.configured) return false;
            return selectedTagIds.length === 0 || selectedTagIds.some(id => row.tagIds.includes(id));
        });

        return result.sort((left, right) => {
            if (sortMode === "pointNo") return left.pointNo.localeCompare(right.pointNo, "zh-Hant", { numeric: true });
            if (sortMode === "area") return left.area.localeCompare(right.area, "zh-Hant") || left.pointIdx - right.pointIdx;
            if (sortMode === "tags") return left.tagNames.localeCompare(right.tagNames, "zh-Hant") || left.pointIdx - right.pointIdx;
            return left.pointIdx - right.pointIdx;
        });
    }, [rows, tagsByPoint, search, areaFilter, configuredFilter, selectedTagIds, sortMode]);

    const areas = useMemo(() => [...new Set(rows.map(row => row.area).filter(Boolean))]
        .sort((a, b) => a.localeCompare(b, "zh-Hant")), [rows]);

    const openTagPicker = async row => {
        if (!canEdit) return;
        if (tags.length === 0) {
            await Swal.fire("尚無標籤", "請先在上方建立 GeoNow 標籤。", "info");
            return;
        }
        const selected = new Set(row.tagIds || []);
        const html = `<div style="text-align:left;max-height:320px;overflow:auto">${tags.map(tag =>
            `<label style="display:block;padding:6px"><input type="checkbox" name="instrument-tag" value="${tag.id}" ${selected.has(tag.id) ? "checked" : ""}> <span style="color:${escapeHtml(tag.color)}">●</span> ${escapeHtml(tag.name)}</label>`
        ).join("")}</div>`;
        const result = await Swal.fire({
            title: `${escapeHtml(row.pointNo)} (#${row.pointIdx}) 的標籤`,
            html,
            showCancelButton: true,
            confirmButtonText: "套用",
            cancelButtonText: "取消",
            preConfirm: () => Array.from(document.querySelectorAll('input[name="instrument-tag"]:checked')).map(x => x.value)
        });
        if (!result.isConfirmed) return;
        const response = await geoNow.api.instrumentTags.replacePointTags(
            token, selectedProject.projectId, row.pointIdx, result.value || []
        );
        if (response?.status !== 1) {
            await Swal.fire("標籤更新失敗", response?.message || "未收到後端回應。", "error");
            return;
        }
        await reloadTags();
    };

    const columns = [
        { title: "#", field: "pointIdx", sorter: "number", frozen: true, width: 80 },
        { title: "儀器名稱", field: "pointNo", sorter: "string", frozen: true },
        { title: "GeoAuto 區域", field: "area", sorter: "string" },
        {
            title: "GeoNow 標籤", field: "tagNames", sorter: "string", minWidth: 160,
            formatter: cell => cell.getValue() || "＋ 設定標籤",
            cellClick: (_event, cell) => openTagPicker(cell.getRow().getData())
        },
        { title: "別名", field: "nickname", editor: canEdit, sorter: "string" },
        { title: "下限", field: "lowerScale", editor: canEdit, sorter: "number", validator: ["numeric"] },
        { title: "上限", field: "upperScale", editor: canEdit, sorter: "number", validator: ["numeric"] },
        { title: "鎖死狀態訊息", field: "statusStaticNote", editor: canEdit, sorter: "string", minWidth: 180 },
        { title: "經度", field: "longtitude", editor: canEdit, sorter: "number", validator: ["numeric"] },
        { title: "緯度", field: "latitude", editor: canEdit, sorter: "number", validator: ["numeric"] },
        { title: "海拔", field: "height", editor: canEdit, sorter: "number", validator: ["numeric"] },
        { title: "隱藏", field: "hidden", editor: canEdit, formatter: "tickCross", sorter: "boolean" },
        {
            title: "狀態", field: "configured", sorter: "boolean",
            formatter: cell => cell.getValue() ? (cell.getData().hasMultipleSettings ? "已設定（多筆）" : "已設定") : "未設定"
        }
    ];

    const saveChanges = async () => {
        const changedRows = rows.filter(row => dirtyPoints.has(row.pointIdx));
        let addItems;
        let updateItems;
        try {
            addItems = changedRows.filter(row => !row.id).map(row => toInstrumentExtendPayload(row, true));
            updateItems = changedRows.filter(row => row.id).map(row => toInstrumentExtendPayload(row, false));
        } catch (error) {
            await Swal.fire("輸入錯誤", error.message, "error");
            return;
        }
        const response = await geoNow.api.instrumentExtends.batchUpdate(token, addItems, updateItems);
        if (response?.status !== 1) {
            await Swal.fire("儲存失敗", response?.message || "未收到後端回應。", "error");
            return;
        }
        await Promise.resolve(onSavedChanges?.());
        await Swal.fire({ title: "已儲存", text: `新增 ${addItems.length} 筆、更新 ${updateItems.length} 筆。`, icon: "success", timer: 1800, showConfirmButton: false });
    };

    const addTag = async () => {
        if (!newTagName.trim()) return;
        const response = await geoNow.api.instrumentTags.add(
            token, selectedProject.projectId, newTagName.trim(), newTagColor
        );
        if (response?.status !== 1) {
            await Swal.fire("新增失敗", response?.message || "未收到後端回應。", "error");
            return;
        }
        setNewTagName("");
        await reloadTags();
    };

    const editTag = async tag => {
        const result = await Swal.fire({
            title: "編輯標籤",
            input: "text",
            inputValue: tag.name,
            showCancelButton: true,
            confirmButtonText: "儲存",
            cancelButtonText: "取消",
            inputValidator: value => !value?.trim() ? "標籤名稱不得空白。" : undefined
        });
        if (!result.isConfirmed) return;
        const response = await geoNow.api.instrumentTags.update(token, tag.id, result.value.trim(), tag.color);
        if (response?.status !== 1) await Swal.fire("更新失敗", response?.message || "未收到後端回應。", "error");
        else await reloadTags();
    };

    const deleteTag = async tag => {
        const answer = await Swal.fire({
            title: `刪除「${tag.name}」？`,
            text: "此標籤在所有儀器上的指派也會一併刪除。",
            icon: "warning",
            showCancelButton: true,
            confirmButtonText: "刪除",
            cancelButtonText: "取消",
            confirmButtonColor: "#d33"
        });
        if (!answer.isConfirmed) return;
        const response = await geoNow.api.instrumentTags.delete(token, tag.id);
        if (response?.status !== 1) await Swal.fire("刪除失敗", response?.message || "未收到後端回應。", "error");
        else await reloadTags();
    };

    return (
        <Card border="light" className="bg-white shadow-sm mb-4">
            <Card.Body>
                <div className="d-flex flex-wrap justify-content-between align-items-center mb-3">
                    <div>
                        <h5 className="mb-1">全儀器設定面板</h5>
                        <small className="text-muted">直接從 SiteDb 儀器清單設定；修改未設定儀器時會自動建立 InsEx。</small>
                    </div>
                    <div>
                        <Button variant="outline-secondary" className="me-2" onClick={onSwitchToClassic}>
                            <FontAwesomeIcon icon={faArrowLeft} className="me-1" /> 傳統設定表
                        </Button>
                        {canEdit && <Button disabled={dirtyPoints.size === 0} onClick={saveChanges}>
                            <FontAwesomeIcon icon={faSave} className="me-1" /> 儲存變更 ({dirtyPoints.size})
                        </Button>}
                    </div>
                </div>

                {canEdit && <Card className="mb-3 bg-light">
                    <Card.Body className="py-3">
                        <div className="d-flex flex-wrap align-items-center mb-2">
                            <FontAwesomeIcon icon={faTags} className="me-2" />
                            <strong className="me-3">GeoNow 標籤管理</strong>
                            {tags.map(tag => <Badge key={tag.id} bg="white" text="dark" className="me-2 mb-1 border" style={{ borderColor: tag.color }}>
                                <span style={{ color: tag.color }}>●</span> {tag.name}
                                <Button variant="link" size="sm" className="p-0 ms-2" onClick={() => editTag(tag)} aria-label={`編輯 ${tag.name}`}>
                                    <FontAwesomeIcon icon={faEdit} />
                                </Button>
                                <Button variant="link" size="sm" className="p-0 ms-2 text-danger" onClick={() => deleteTag(tag)} aria-label={`刪除 ${tag.name}`}>
                                    <FontAwesomeIcon icon={faTrash} />
                                </Button>
                            </Badge>)}
                        </div>
                        <InputGroup style={{ maxWidth: 520 }}>
                            <Form.Control value={newTagName} maxLength={50} placeholder="新增標籤名稱" onChange={e => setNewTagName(e.target.value)} onKeyDown={e => { if (e.key === "Enter") { e.preventDefault(); addTag(); } }} />
                            <Form.Control type="color" value={newTagColor} onChange={e => setNewTagColor(e.target.value)} style={{ maxWidth: 55 }} title="標籤顏色" />
                            <Button onClick={addTag} disabled={!newTagName.trim()}><FontAwesomeIcon icon={faPlus} /> 新增</Button>
                        </InputGroup>
                    </Card.Body>
                </Card>}

                <Row className="g-2 mb-3">
                    <Col md={6} xl={3}><Form.Control placeholder="模糊搜尋點號、名稱、區域、別名、標籤" value={search} onChange={e => setSearch(e.target.value)} /></Col>
                    <Col md={6} xl={2}><Form.Select value={areaFilter} onChange={e => setAreaFilter(e.target.value)}><option value="all">全部 GeoAuto 區域</option>{areas.map(area => <option key={area}>{area}</option>)}</Form.Select></Col>
                    <Col md={6} xl={2}><Form.Select value={configuredFilter} onChange={e => setConfiguredFilter(e.target.value)}><option value="all">全部設定狀態</option><option value="configured">已有 InsEx</option><option value="unconfigured">尚未設定</option></Form.Select></Col>
                    <Col md={6} xl={3}><Select isMulti isClearable placeholder="依標籤過濾（可多選）" options={tags.map(tag => ({ value: tag.id, label: tag.name }))} onChange={items => setSelectedTagIds((items || []).map(item => item.value))} /></Col>
                    <Col md={6} xl={2}><Form.Select value={sortMode} onChange={e => setSortMode(e.target.value)}><option value="pointIdx">依 PointIdx 排序</option><option value="pointNo">依儀器名稱排序</option><option value="area">依區域排序</option><option value="tags">依標籤排序</option></Form.Select></Col>
                </Row>

                <div className="mb-2 text-muted">顯示 {displayRows.length} / {rows.length} 台儀器；點選「GeoNow 標籤」欄即可多選標籤。</div>
                <ReactTabulator
                    data={displayRows}
                    columns={columns}
                    options={{ layout: "fitData", pagination: "local", paginationSize: 20, paginationSizeSelector: [20, 50, 100, true], movableColumns: true }}
                    events={{
                        cellEdited: cell => {
                            const changed = { ...cell.getRow().getData() };
                            setRows(current => current.map(row => row.pointIdx === changed.pointIdx ? { ...row, ...changed } : row));
                            setDirtyPoints(current => new Set(current).add(changed.pointIdx));
                            cell.getRow().getElement().style.backgroundColor = "#fff3cd";
                        }
                    }}
                />
            </Card.Body>
        </Card>
    );
}
